'use client';

import {isBrowser, isTalonEnabled, isTalonReady} from '@/lib/utils/page.utils';
import {useEffect, useState, useCallback, useMemo, useRef} from 'react';
import {useNetworkStatus} from '../browser';

interface TalonState {
	inProgress: boolean;
	isReady: boolean;
	flowId: string;
	solveToken: string;
	executedAt: number;
	completedAt: number;
}

const FLOW_ID = process.env.NEXT_PUBLIC_TALON_FLOW_ID;

type TalonCallback = (solveToken?: string) => void;

export const useTalon = (): {
	initCaptcha: (cb: TalonCallback) => void;
	talonState: TalonState;
} => {
	const initialState = useMemo<TalonState>(
		() => ({
			isReady: isBrowser() ? !!window.talon : false,
			inProgress: false,
			solveToken: '',
			flowId: FLOW_ID || '',
			executedAt: -1,
			completedAt: -1
		}),
		[]
	);

	const [talonState, setTalonState] = useState<TalonState>(initialState);
	const callbackRef = useRef<TalonCallback>(undefined);
	const flowId = talonState.flowId;
	const talonEnabled = isTalonEnabled();
	const reset = () => setTalonState(initialState);
	const isOnline = useNetworkStatus();

	// onError doesn’t reliably handle offline cases - when the connection drops, the state isn’t resetting or propagating correctly to the parent component.
	useEffect(() => {
		if (!isOnline && talonState.inProgress) {
			reset();
		} // This resets properly if we go offline.
	}, [isOnline]);

	useEffect(() => {
		if (!talonEnabled || !isBrowser()) {
			if (process.env.NODE_ENV === 'development') {
				console.info(
					`[DEV] Talon is not working : enableTalon ${talonEnabled} flowId: ${flowId}`
				);
			}
			return;
		}

		/** Already loaded */
		if (isTalonReady()) {
			return;
		}

		/** @see Talon.tsx component */
		// const loadTalon = () => {
		// 	return new Promise<boolean>((resolve, reject) => {
		// 		if (!window) return reject(new Error("Failed to load talon on dom"));
		// 		if (window.talon?.ready) return resolve(true);
		// 		const url = process.env.NEXT_PUBLIC_TALON_BASE || "https://talon-website-prod.ecosec.on.epicgames.com";
		// 		const script = window.document.createElement("script");
		// 		script.src = `${url}/talon_sdk.js`;
		// 		script.async = true;
		// 		script.defer = true;
		// 		script.nonce = nonce;
		// 		script.onload = () => {
		// 			resolve(true);
		// 		};
		// 		script.onerror = () => {
		// 			reject(new Error("Failed to loadTalon from cdn"));
		// 		};
		// 		window.document.body.appendChild(script);
		// 	});
		// };

		window.talon?.load({
			flow: flowId,
			onReady: () => {
				console.log('Talon sdk is ready.');
				window.talon!.ready = true;
				setTalonState((prev) => ({...prev, isReady: true}));
			},
			onClosed: () => {
				console.log('captcha closed.');
				dispatchEvent(new CustomEvent('talonReset'));
			},
			onError: (err) => {
				console.error('captcha failed with: ', err);
				dispatchEvent(new CustomEvent('talonReset'));
			},
			onComplete: (solveToken) => {
				console.log('captcha completed.');
				dispatchEvent(
					new CustomEvent('talonCaptchaComplete', {bubbles: false, detail: {solveToken}})
				);
			},
			onExpired: () => {
				try {
					console.log('captcha re-running');
					window.talon?.execute(flowId);
					setTalonState((prev) => ({...prev, inProgress: true, executedAt: Date.now()}));
				} catch {
					console.error('onExpired Talon Captcha failed to execute.');
					dispatchEvent(new CustomEvent('talonReset'));
				}
			}
		});
	}, [flowId, talonEnabled]);

	useEffect(() => {
		if (!isBrowser()) return;
		if (typeof callbackRef.current !== 'function') return;

		const handleCaptchaComplete = (e: Event) => {
			const customEvent = e as CustomEvent<{solveToken: string}>;
			const {solveToken} = customEvent?.detail || {};
			setTalonState((prev) => ({
				...prev,
				inProgress: false,
				solveToken,
				completedAt: Date.now()
			}));
			callbackRef.current!(solveToken);
		};

		const cleanup = () => {
			window.removeEventListener('talonCaptchaComplete', handleCaptchaComplete);
			window.removeEventListener('talonReset', reset);
		};

		cleanup(); // we remove if it exists, from any previous use case

		window.addEventListener('talonCaptchaComplete', handleCaptchaComplete);
		window.addEventListener('talonReset', reset);

		return () => {
			cleanup();
		};
	}, [callbackRef.current]);

	return {
		talonState,
		initCaptcha: useCallback(
			(trigger: TalonCallback) => {
				if (!talonEnabled) {
					return trigger();
				}

				try {
					console.log('captcha started...');
					callbackRef.current = trigger; // must be before execute
					window.talon?.execute(flowId);
					setTalonState((prev) => ({...prev, inProgress: true, executedAt: Date.now()}));
				} catch {
					console.error('initCaptcha Talon Captcha failed to execute.');
					reset();
					trigger();
				}
			},
			[flowId, talonEnabled]
		)
	};
};
